import { getSmallKidAgeThreshold, replacePlaceholders, RootState } from '@msc-b2c/shared-utils';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import HeadsUpInfo, { HeadsUpInfoProps, HeadsUpMessagePriority } from './HeadsUpInfo';
import { HeadsUpIconAlignment, PriceBasisEnum } from '@msc-b2c/interfaces/enums';
import { GetBookingMyMscResponse, PassengerI } from '@msc-b2c/interfaces/interfaces';
import {
  hasObsCombinedRelatedPackages,
  hasSomeObsPassengersNotApplicableForPaxNotEligibleBanner,
  isAdultOrSenior,
  isUnderSmallKidAgeThreshold,
} from '../../utils/PostBookingResultsUtilities';
import styles from './headsupinfo.module.scss';

interface HeadsUpContainerProps {
  checkedPassengers?: Record<string, Record<string, boolean>>;
  bookingData?: GetBookingMyMscResponse;
}

function hasHeadsUpContent({ title, description }: HeadsUpInfoProps) {
  return !!title?.trim() || !!description?.trim();
}

function HeadsUpContainer({ checkedPassengers, bookingData }: Readonly<HeadsUpContainerProps>) {
  const prepaidCardType = useSelector((state: RootState) => state.myAreaCommonConfig.prepaidType);
  const headsUpMessagesConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig.headsUpMessagesConfig
  );
  const myAreaExcursionCardData = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaExcursionCard
  );
  const myAreaOBSCardData = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaOBSCard
  );
  const { shopExcursion, shopOBS } = useSelector((state: RootState) => state.myCruise);
  const [stickyHeadsUpInfo, setStickyHeadsUpInfo] = useState<HeadsUpInfoProps[]>([]);
  const [nonStickyHeadsUpInfo, setNonStickyHeadsUpInfo] = useState<HeadsUpInfoProps[]>([]);
  const smallKidAgeThreshold = getSmallKidAgeThreshold();

  const excursionPassengerPrices = useMemo(() => {
    return myAreaExcursionCardData?.code === shopExcursion?.code
      ? shopExcursion?.prices?.perPassenger || []
      : [];
  }, [myAreaExcursionCardData?.code, shopExcursion]);

  const obsPassengerPrices = useMemo(() => {
    return myAreaOBSCardData?.code === shopOBS?.code ? shopOBS?.prices?.perPassenger || [] : [];
  }, [myAreaOBSCardData?.code, shopOBS]);

  const passengerData = useMemo(() => bookingData?.passengers || [], [bookingData]);

  const isCardAllInclusive = myAreaOBSCardData?.prepaidItems?.isAllInclusive;
  const isCardPerCabin =
    prepaidCardType === 'Excursions'
      ? myAreaExcursionCardData?.priceBasis === PriceBasisEnum.PER_CABIN
      : myAreaOBSCardData?.priceBasis === PriceBasisEnum.PER_CABIN;
  const isCombinedObsPackage = hasObsCombinedRelatedPackages(myAreaOBSCardData);

  useEffect(() => {
    if (!headsUpMessagesConfig) {
      return;
    }

    const headsUpInfo: HeadsUpInfoProps[] = [
      {
        icon: headsUpMessagesConfig.paxNotApplicableIcon,
        iconAlignment: HeadsUpIconAlignment.TOP,
        title: headsUpMessagesConfig.paxNotApplicableTitle,
        description: headsUpMessagesConfig.paxNotApplicableDesc,
        isSticky: headsUpMessagesConfig.stickyPaxNotApplicable,
        priority: HeadsUpMessagePriority.DEFAULT,
        visibilityCondition: () =>
          !headsUpMessagesConfig.hidePaxNotApplicable &&
          ((prepaidCardType === 'OBS' &&
            hasSomeObsPassengersNotApplicableForPaxNotEligibleBanner(
              obsPassengerPrices,
              isCombinedObsPackage
            )) ||
            (prepaidCardType === 'Excursions' &&
              excursionPassengerPrices.some((p) => !p.isApplicable))),
      },
      {
        icon: headsUpMessagesConfig.allInclusiveIcon,
        iconAlignment: HeadsUpIconAlignment.TOP,
        title: headsUpMessagesConfig.allInclusiveTitle,
        description: headsUpMessagesConfig.allInclusiveDesc,
        isSticky: headsUpMessagesConfig.stickyAllInclusive,
        priority: HeadsUpMessagePriority.DEFAULT,
        visibilityCondition: () =>
          !headsUpMessagesConfig.hideAllInclusive && (isCardAllInclusive || isCardPerCabin),
      },
      {
        icon: headsUpMessagesConfig.couplePackageIcon,
        iconAlignment: HeadsUpIconAlignment.TOP,
        title: headsUpMessagesConfig.couplePackageTitle,
        description: headsUpMessagesConfig.couplePackageDesc,
        isSticky: headsUpMessagesConfig.stickyCouple,
        priority: HeadsUpMessagePriority.DEFAULT,
        visibilityCondition: () =>
          prepaidCardType === 'OBS' &&
          !headsUpMessagesConfig.hideCouple &&
          myAreaOBSCardData?.prepaidItems?.isPerCouple,
      },
      {
        icon: headsUpMessagesConfig.nonPersonalMainIcon,
        title: headsUpMessagesConfig.nonPersonalMainTitle,
        description: headsUpMessagesConfig.nonPersonalMainDesc,
        isSticky: headsUpMessagesConfig.stickyNonPersonalMain,
        priority: HeadsUpMessagePriority.DEFAULT,
        visibilityCondition: () =>
          prepaidCardType === 'OBS' &&
          !headsUpMessagesConfig.hideNonPersonalMain &&
          (myAreaOBSCardData?.type.code === 'PHOTO' || myAreaOBSCardData?.type.code === 'LAUNDR'),
      },
      {
        icon: headsUpMessagesConfig.combinedPackagesIcon,
        iconAlignment: HeadsUpIconAlignment.TOP,
        title: headsUpMessagesConfig.combinedPackagesTitle,
        description: headsUpMessagesConfig.combinedPackagesDesc,
        isSticky: headsUpMessagesConfig.stickyCombined,
        priority: HeadsUpMessagePriority.DEFAULT,
        visibilityCondition: () => {
          if (
            headsUpMessagesConfig.hideCombined ||
            prepaidCardType !== 'OBS' ||
            !isCardAllInclusive
          ) {
            return false;
          }

          const hasAdultWithServiceCodes =
            passengerData.some(isAdultOrSenior) &&
            !!myAreaOBSCardData?.prepaidItems?.serviceCodes?.adults?.length;

          const hasMinorWithServiceCodes =
            passengerData.some((p) => !isAdultOrSenior(p)) &&
            !!myAreaOBSCardData?.prepaidItems?.serviceCodes?.minors?.length;

          return hasAdultWithServiceCodes || hasMinorWithServiceCodes;
        },
      },
      {
        icon: headsUpMessagesConfig.underSmallKidAgeThresholdIcon,
        iconAlignment: HeadsUpIconAlignment.TOP,
        title: headsUpMessagesConfig.underSmallKidAgeThresholdTitle,
        priority: HeadsUpMessagePriority.HIGH,
        isSticky: headsUpMessagesConfig.stickyUnderSmallKidAgeThreshold,
        description: replacePlaceholders(headsUpMessagesConfig.underSmallKidAgeThresholdDesc, {
          kid_yo: smallKidAgeThreshold.toString(),
        }),
        visibilityCondition: () =>
          prepaidCardType === 'Excursions' &&
          !headsUpMessagesConfig.hideUnderSmallKidAgeThreshold &&
          passengerData.some((passenger: PassengerI) =>
            isUnderSmallKidAgeThreshold(passenger, smallKidAgeThreshold, bookingData)
          ),
      },
      {
        icon: headsUpMessagesConfig.nonPersonalSecondaryIcon,
        iconAlignment: HeadsUpIconAlignment.TOP,
        title: headsUpMessagesConfig.nonPersonalSecondaryTitle,
        description: headsUpMessagesConfig.nonPersonalSecondaryDesc,
        isSticky: headsUpMessagesConfig.stickyNonPersonalSecondary,
        priority: HeadsUpMessagePriority.DEFAULT,
        visibilityCondition: () =>
          (myAreaOBSCardData?.type.code === 'PHOTO' || myAreaOBSCardData?.type.code === 'LAUNDR') &&
          prepaidCardType === 'OBS' &&
          !headsUpMessagesConfig.hideNonPersonalSecondary &&
          checkedPassengers != null &&
          Object.values(Object.values(checkedPassengers)[0] || {}).filter(Boolean).length > 1,
      },
    ];

    setStickyHeadsUpInfo(headsUpInfo.filter((headsUp) => headsUp.isSticky));
    setNonStickyHeadsUpInfo(
      headsUpInfo.filter((headsUp) => !headsUp.isSticky).map(({ isSticky, ...headsUp }) => headsUp)
    );
  }, [
    headsUpMessagesConfig,
    prepaidCardType,
    obsPassengerPrices,
    excursionPassengerPrices,
    isCardAllInclusive,
    isCombinedObsPackage,
    myAreaOBSCardData,
    checkedPassengers,
    passengerData,
    bookingData,
    smallKidAgeThreshold,
  ]);

  const visibleStickyHeadsUps = stickyHeadsUpInfo
    .filter((headsUp) => hasHeadsUpContent(headsUp) && headsUp.visibilityCondition())
    .sort((headsUp) => (headsUp.priority === HeadsUpMessagePriority.HIGH ? -1 : 1));

  const visibleNonStickyHeadsUps = nonStickyHeadsUpInfo
    .filter((headsUp) => hasHeadsUpContent(headsUp) && headsUp.visibilityCondition())
    .sort((headsUp) => (headsUp.priority === HeadsUpMessagePriority.HIGH ? -1 : 1));

  return (
    <>
      <div className="headsUpContainer__wrapper">
        {visibleStickyHeadsUps.length > 0 && (
          <div className="headsUpContainer__stickable">
            <div className={`${styles['headsUpContainer__stickable__messages']}`}>
              {visibleStickyHeadsUps.map((headsUp) => (
                <HeadsUpInfo
                  key={`sticky-${headsUp.description}`}
                  {...headsUp}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      {visibleNonStickyHeadsUps.length > 0 && (
        <div className="headsUpContainer__non-stickable">
          {visibleNonStickyHeadsUps.map((headsUp) => (
            <HeadsUpInfo
              key={`non-sticky-${headsUp.description}`}
              {...headsUp}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default HeadsUpContainer;
